/**
 * 비공개·기능성 경로의 색인 정책 단일 소스.
 *
 * 영구지침: docs/develop/seo/ssr-spec.md §16.1, §16.2
 *
 * 배경(GSC 2026-09-21): /recommend·/bookmarks·/compare·/mypage·/login·/signup·/admin·
 * /facility·/facility/login 이 canonical 없이 200 을 응답해 "표준 없는 중복 페이지"
 * 후보가 됐다. CSR 전용 경로는 server.ts 가 React 를 거치지 않고 빈 index.html 을 주고,
 * SSR 대상이어도 RequireAuth(SSR 에서 null)·lazy(Suspense 폴백) 뒤의 페이지 내부 <SEO> 는
 * 서버 렌더에서 실행되지 않는다.
 *
 * 순수 함수(별칭 import 없음) — `npm test`(tsx --test)와 server.ts(tsx) 양쪽에서 쓴다.
 */
import { normalizeSsrPath } from '../ssr/normalizeSsrPath'

/** robots 값 — SEO.tsx 의 noindex prop 출력과 동일 문자열을 유지한다 */
export const NOINDEX_ROBOTS = 'noindex, follow'

export interface NoindexRoute {
  /**
   * 경로 prefix.
   *
   * 매칭은 `pathname === prefix || pathname.startsWith(prefix + '/')` — 부분 문자열
   * 매칭이 아니다. `/facility` 가 `/facilities/**`(색인 대상 핵심 자산)를 삼키지 않도록
   * 하는 것이 이 규칙의 핵심이다.
   */
  prefix: string
  /** 페이지 title (브랜드 접미사는 SEO 컴포넌트가 붙인다) */
  title: string
}

/** 영구지침 §16.1 표와 1:1 */
export const NOINDEX_ROUTES: readonly NoindexRoute[] = [
  { prefix: '/login', title: '로그인' },
  { prefix: '/signup', title: '회원가입' },
  { prefix: '/forgot-password', title: '비밀번호 찾기' },
  { prefix: '/reset-password', title: '비밀번호 재설정' },
  { prefix: '/mypage', title: '마이페이지' },
  { prefix: '/admin', title: '관리자' },
  { prefix: '/facility', title: '시설관리자' },
  { prefix: '/auth', title: '인증 처리' },
  { prefix: '/oauth2', title: '외부 서비스 연동' },
  { prefix: '/recommend', title: 'AI 맞춤 요양시설 추천' },
  { prefix: '/bookmarks', title: '관심 시설' },
  { prefix: '/compare', title: '시설 비교' },
  { prefix: '/unsubscribe', title: '수신거부' },
  { prefix: '/shop', title: '쇼핑몰 이동' },
]

export interface NoindexRouteMatch {
  /** self-canonical 에 쓸 경로 — 쿼리·해시·끝 슬래시 없음 */
  canonicalPath: string
  /** 페이지 title */
  title: string
}

/**
 * 경로가 비공개·기능성 경로군에 속하면 self-canonical 경로와 title 을, 아니면 null.
 *
 * null 이면 호출부는 **아무 메타 태그도 내지 않는다** — 색인 대상 페이지(시설 상세·지역·
 * 가이드·블로그·커뮤니티)의 기존 SEO 를 절대 건드리지 않기 위한 계약이다.
 */
export function findNoindexRoute(pathnameOrUrl: string): NoindexRouteMatch | null {
  const pathname = normalizeSsrPath(pathnameOrUrl)
  const matched = NOINDEX_ROUTES.find(
    ({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
  if (!matched) return null
  return { canonicalPath: pathname, title: matched.title }
}
